import React, { useContext, useState } from 'react';
import { AppContext } from '../../App';
import { Sparkles, RefreshCw, Cpu } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AIInsightsPanel = () => {
  const { dashboardData, selectedProvince } = useContext(AppContext);
  const [refreshing, setRefreshing] = useState(false);
  const [insights, setInsights] = useState(null);
  
  if (!dashboardData) return null;
  
  const displayInsights = insights || dashboardData.ai_insights;

  const refreshInsights = async () => {
    setRefreshing(true);
    try {
      const params = selectedProvince ? `?province=${selectedProvince}` : '';
      const response = await axios.get(`${API}/ai-insights${params}`);
      setInsights(response.data);
      toast.success('AI insights refreshed');
    } catch (error) {
      toast.error('Failed to refresh insights');
    } finally {
      setRefreshing(false);
    }
  };

  const getCategoryStyle = (category) => {
    const styles = {
      'PRICING': { bg: 'bg-[#A855F7]/20', text: 'text-[#A855F7]', border: 'border-l-[#A855F7]' },
      'INVENTORY': { bg: 'bg-[#3B82F6]/20', text: 'text-[#3B82F6]', border: 'border-l-[#3B82F6]' },
      'DEMAND': { bg: 'bg-[#22C55E]/20', text: 'text-[#22C55E]', border: 'border-l-[#22C55E]' },
      'OPPORTUNITY': { bg: 'bg-[#F97316]/20', text: 'text-[#F97316]', border: 'border-l-[#F97316]' }
    };
    return styles[category] || styles['INVENTORY'];
  };

  return (
    <div className="dashboard-card p-6 h-full" data-testid="ai-insights">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#F97316]/10 rounded-lg">
            <Sparkles className="w-5 h-5 text-[#F97316]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">AI Insights</h3>
            <div className="flex items-center gap-1 text-xs text-[#A1A1AA]">
              <Cpu className="w-3 h-3" />
              <span>Claude 4 Opus</span>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshInsights}
          disabled={refreshing}
          className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
          data-testid="refresh-insights-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      <div className="space-y-3">
        {displayInsights.slice(0, 4).map((insight, index) => {
          const style = getCategoryStyle(insight.category);
          
          return (
            <div 
              key={insight.id || index}
              className={`p-4 bg-[#27272A]/50 rounded-lg border-l-4 ${style.border} transition-all hover:bg-[#27272A]/70`}
              data-testid={`insight-${index}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded ${style.bg} ${style.text}`}>
                  {insight.category}
                </span>
                {insight.priority === 'high' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-[#EF4444]/20 text-[#EF4444]">
                    Priority
                  </span>
                )}
              </div>
              <p className="text-sm text-[#E4E4E7] leading-relaxed">{insight.insight}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AIInsightsPanel;
